import { useMemo } from 'react';
import { hasValidWhatsApp } from '../components/Filters/phoneValidation';
import { processLeadsWithWhatsAppInfo } from '../components/Filters/phoneValidation';

/**
 * Filters leads based on multiple criteria
 * @param {Array} chartData - The original array of lead data
 * @param {Object} filters - An object containing filter parameters
 * @param {string} [filters.situacaoFilter='Todos'] - Filter by internal situation
 * @param {string} [filters.idLeadFilter=''] - Filter by lead ID (partial, case-insensitive)
 * @param {string} [filters.nomeFilter=''] - Filter by name (partial, case-insensitive)
 * @param {string} [filters.whatsAppIdFilter=''] - Filter by WhatsApp ID (partial, case-insensitive)
 * @returns {Array} Filtered array of leads
 */
export function filterLeads(chartData, filters = {}) {
  // Destructure filters with default values
  const {
    situacaoFilter = 'Todos',
    idLeadFilter = '',
    nomeFilter = '',
    whatsAppIdFilter = ''
  } = filters;

  // If data is not loaded or not an array, return empty list
  if (!chartData || !Array.isArray(chartData)) return [];

  let filteredData = chartData;

  // Filter by situation
  if (situacaoFilter !== 'Todos') {
    filteredData = filteredData.filter(lead => {
      return lead.situacaoInterna === situacaoFilter;
    });
  }

  // Filter by lead ID (partial and case-insensitive)
  if (idLeadFilter.trim() !== '') {
    filteredData = filteredData.filter(lead =>
      String(lead.idLead).toLowerCase().includes(idLeadFilter.toLowerCase())
    );
  }

  // Filter by name (partial and case-insensitive)
  if (nomeFilter.trim() !== '') {
    filteredData = filteredData.filter(lead =>
      lead.nome && lead.nome.toLowerCase().includes(nomeFilter.toLowerCase())
    );
  }

  // Filter by WhatsApp ID (partial and case-insensitive)
  if (whatsAppIdFilter.trim() !== '') {
    filteredData = filteredData.filter(lead =>
      lead.whatsAppId && lead.whatsAppId.toLowerCase().includes(whatsAppIdFilter.toLowerCase())
    );
  }

  return filteredData;
}

/**
 * Hook personalizado para filtrar leads com base em vários critérios
 * Inclui validação automática de WhatsApp e múltiplos filtros
 * 
 * @param {Array<Object>} chartData - Array de dados de leads brutos
 * @param {Object} filters - Objeto contendo os filtros aplicados
 * @param {string} filters.situacaoFilter - Filtro por situação
 * @param {string} filters.idLeadFilter - Filtro por ID do lead
 * @param {string} filters.nomeFilter - Filtro por nome
 * @param {string} filters.whatsAppIdFilter - Filtro por WhatsApp ID
 * @param {string} filters.emailFilter - Filtro por email
 * @param {string} filters.telefoneFilter - Filtro por telefone
 * @param {boolean} filters.whatsAppValidationEnabled - Se deve aplicar validação de WhatsApp
 * @param {boolean} filters.showOnlyValidWhatsApp - Mostrar apenas WhatsApp válidos
 * @param {Array<string>} filters.situacoesExcluidas - Situações a serem excluídas
 * @returns {Array<Object>} Array de leads filtrados e processados
 */
export function useFilteredLeads(chartData, filters = {}) {
  const {
    situacaoFilter = 'Todos',
    idLeadFilter = '',
    nomeFilter = '',
    whatsAppIdFilter = '',
    emailFilter = '',
    telefoneFilter = '',
    whatsAppValidationEnabled = true,
    showOnlyValidWhatsApp = false,
    situacoesExcluidas = [],
    descarteFilter,
    empreendimentoFilter,
    startDate,
    endDate
  } = filters;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filteredLeads = useMemo(() => {
    // Validação inicial dos dados
    if (!chartData || !Array.isArray(chartData) || chartData.length === 0) {
      return [];
    }

    // Primeiro, aplica a verificação de WhatsApp se estiver habilitada
    let processedData = whatsAppValidationEnabled
      ? processLeadsWithWhatsAppInfo(chartData)
      : chartData;

    // Aplica os filtros sequencialmente
    let filtered = processedData;

    // Filtro por situações excluídas (aplicado primeiro)
    if (situacoesExcluidas && situacoesExcluidas.length > 0) {
      filtered = filtered.filter(lead =>
        !situacoesExcluidas.includes(lead.situacaoInterna)
      );
    }

    // Filtro por situação específica
    if (situacaoFilter && situacaoFilter !== 'Todos') {
      if (situacaoFilter === 'Não é WhatsApp') {
        // Filtra leads que não têm WhatsApp válido
        filtered = filtered.filter(lead => !hasValidWhatsApp(lead));
      } else {
        // Para outras situações, filtra normalmente
        filtered = filtered.filter(lead =>
          lead.situacaoInterna === situacaoFilter
        );
      }
    }

    // Filtro por ID do lead
    if (idLeadFilter && idLeadFilter.trim() !== '') {
      const searchTerm = idLeadFilter.toLowerCase().trim();
      filtered = filtered.filter(lead =>
        lead.idLead &&
        lead.idLead.toString().toLowerCase().includes(searchTerm)
      );
    }

    // Filtro por nome (busca parcial, insensível a caso)
    if (nomeFilter && nomeFilter.trim() !== '') {
      const searchTerm = nomeFilter.toLowerCase().trim();
      filtered = filtered.filter(lead =>
        lead.nome &&
        lead.nome.toLowerCase().includes(searchTerm)
      );
    }

    // Filtro por WhatsApp ID
    if (whatsAppIdFilter && whatsAppIdFilter.trim() !== '') {
      const searchTerm = whatsAppIdFilter.toLowerCase().trim();
      filtered = filtered.filter(lead =>
        lead.whatsAppId &&
        lead.whatsAppId.toString().toLowerCase().includes(searchTerm)
      );
    }

    // Filtro por email
    if (emailFilter && emailFilter.trim() !== '') {
      const searchTerm = emailFilter.toLowerCase().trim();
      filtered = filtered.filter(lead =>
        lead.email &&
        lead.email.toLowerCase().includes(searchTerm)
      );
    }

    // Filtro por telefone
    if (telefoneFilter && telefoneFilter.trim() !== '') {
      const searchTerm = telefoneFilter.replace(/\D/g, ''); // Remove caracteres não numéricos
      filtered = filtered.filter(lead => {
        const telefone = lead.telefone || lead.whatsapp || '';
        const telefoneNumerico = telefone.replace(/\D/g, '');
        return telefoneNumerico.includes(searchTerm);
      });
    }

    // Filtro por validação de WhatsApp
    if (showOnlyValidWhatsApp && whatsAppValidationEnabled) {
      filtered = filtered.filter(lead => hasValidWhatsApp(lead));
    }

    // Filtro por motivo de descarte
    if (descarteFilter && descarteFilter !== 'Todos') {
      if (descarteFilter === 'Sem Motivo') {
        // Filtra leads sem motivo de descarte definido
        filtered = filtered.filter(lead =>
          !lead.motivoDescarte || lead.motivoDescarte.trim() === ''
        );
      } else {
        // Filtra leads com motivo de descarte correspondente
        filtered = filtered.filter(lead =>
          (lead.motivoDescarte || '').toLowerCase().trim() === descarteFilter.toLowerCase().trim()
        );
      }
    }

    // ADICIONAR - Filtro por empreendimento
    if (empreendimentoFilter && empreendimentoFilter.trim() !== '') {
      filtered = filtered.filter(lead =>
        lead.nomeEmpreendimento &&
        lead.nomeEmpreendimento.toLowerCase().trim() === empreendimentoFilter.toLowerCase().trim()
      );
    }

    if (startDate || endDate) {
      filtered = filtered.filter(lead => {
        if (!lead.dataHoraCadastroAtualizado) return false;

        // Use split com espaço, não com 'T'
        const leadDate = lead.dataHoraCadastroAtualizado.split(' ')[0];

        if (startDate && leadDate < startDate) return false;
        if (endDate && leadDate > endDate) return false;

        return true;
      });
    }

    return filtered;
  }, [
    situacaoFilter,
    idLeadFilter,
    nomeFilter,
    whatsAppIdFilter,
    descarteFilter,
    empreendimentoFilter,  // ADICIONAR ESTA LINHA
    emailFilter,
    telefoneFilter,
    whatsAppValidationEnabled,
    showOnlyValidWhatsApp,
    situacoesExcluidas,
    startDate,
    endDate,
    chartData
  ]);

  return filteredLeads;
}